import { Alert } from '@cmsgov/ds-medicare-gov';
import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { allow } from '../../common/classes/allow';
import Button from '../../common/components/button';
import DateFields from '../../common/components/date.fields';
import Div from '../../common/components/div';
import { getChatUrl } from '../../common/functions/get.chat.url';
import H1 from '../../common/components/h1';
import InputField from '../../common/components/input.field';
import MedicareNumberDialog from '../../common/components/medicare.number.dialog';
import MedicareNumberLabel from '../../common/components/medicare.number.label';
import StackingButtons from '../../common/components/stacking.buttons';
import SubHeading from '../../common/components/sub.heading.section';
import SuffixDropdown from '../../common/components/suffix.dropdown';
import TranslatedSpan from '../../common/components/translated.span';
import ValidationAlertMessage from '../../common/components/validation.alert.message';
import ValidationAlerts from '../../common/components/validation.alerts';
import capitalizeFirstLetter from '../../common/functions/capitalize.first.letter';
import { isValidMedicareNumber } from '../../common/functions/is.valid.medicare.number';
import { isValidYear } from '../../common/functions/is.valid.year';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useFindAccountApi from '../../common/hooks/use.find.account.api';
import useTrait from '../../common/hooks/use.trait';
import useViewport from '../../common/hooks/use.viewport';
import is from '../../common/objects/is';
import dateFieldValue from '../../common/objects/models/date.field.value';
import the from '../../common/objects/the';

interface InitData {
    dateOfBirth?: any
    lastName?: string
    medicareNumber?: string
    suffix?: string
}

interface EnterUserInfoSectionProps {
    initData?: InitData
    showPageCallBack: (num: number) => void
    setUserName: (name: string) => void
    setCreatedAt: (val: any) => void
}

export default function EnterUserInfoSection(props: EnterUserInfoSectionProps) {
    const initData: InitData = props.initData || {};

    const [showFlash, setShowFlash] = useState(false);
    const [errorMessage, setErrorMessage] = useState<JSX.Element | string>('');
    const [dayRef, setDayRef] = useState();
    const [lastNameRef, setLastNameRef] = useState();
    const [medicareNumberRef, setMedicareNumberRef] = useState();
    const [monthRef, setMonthRef] = useState();
    const [yearRef, setYearRef] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const dateOfBirth = useTrait(initData.dateOfBirth || dateFieldValue);
    const dialogIsOpen = useTrait(false);
    const findAccountApi = useFindAccountApi();
    const history = useHistory();
    const lastName = useTrait(initData.lastName || '');
    const lastNameError = useTrait(false);
    const medicareNumber = useTrait(initData.medicareNumber || '');
    const medicareNumberError = useTrait(false);
    const suffix = useTrait(initData.suffix || '');
    const validateEntireForm = useTrait(false);
    const {width} = useViewport();

    const day = translate('day');
    const month = translate('month');
    const year = translate('year');

    const formStart = useFormStart('account_found');
    const conversion = useConversion('verify account access', 'account_found');

    const getCancelButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'outlined'}
                onClick={() => history.push('/login')}
                size={'small'}
            >
                <TranslatedSpan english={'Cancel'}/>
            </Button>
        </>;
    };

    const getFindMyAccountButton = () => {
        return <>
            <Button
                color={'teal'}
                displayAs={'button'}
                fill={'solid'}
                onClick={handleFindAccountButtonClick}
                size={'small'}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'Find My Account'}/>
            </Button>
        </>;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (!validateMedicareNumber()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your Medicare Number.')}
                    fieldReference={medicareNumberRef}
                    linkText={translate('Medicare Number')}
                />
            );
        }
        if (!validateLastName()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter your last name.')}
                    fieldReference={lastNameRef}
                    linkText={translate('Last name')}
                />
            );
        }
        if (dateOfBirth.get().month === '') {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}
                    fieldReference={monthRef}
                    linkText={capitalizeFirstLetter(translate('month'))}
                />
            );
        }
        if (dateOfBirth.get().day === '') {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}
                    fieldReference={dayRef}
                    linkText={capitalizeFirstLetter(translate('day'))}
                />
            );
        }
        if (!isValidYear(dateOfBirth.get().year)) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}
                    fieldReference={yearRef}
                    linkText={capitalizeFirstLetter(translate('year'))}
                />
            );
        }

        return messages;
    };

    const handleFindAccountButtonClick = () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);

        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            errorFieldRef.focus();
            setIsLoading(false);
            return;
        }
        let lastNameVal = lastName.get();
        if (suffix.get()) {
            lastNameVal += ' ' + suffix.get();
        }
        findAccountApi.findAccount(medicareNumber.get(), dateOfBirth.get(), lastNameVal, the.defaultHeader, (result: any) => {
            setIsLoading(false);

            if (!result) {
                setShowFlash(true);
                const message = translate(`We can't process your request at this time. We're sorry for any inconvenience. Please try again.`);
                conversion.failAPIIntermediate(message);
                conversion.failAPI(message);
                return setErrorMessage(message);
            }

            if (result.code === 404) {
                conversion.failAPIIntermediate(translate('No account in our records'));
                conversion.failAPI(translate('No account in our records'));
                props.showPageCallBack(2);
            } else if (result.code === 400) {
                let analyticsMessage;
                let message;
                const isLocked = result.errors.some((e: any) => e.name === 'BadRequest' && e.message.includes('locked'));
                const isDisabled = result.errors.some((e: any) => e.name === 'BadRequest' && e.message.includes('disabled'));
                if (isLocked) {
                    message = <>
                        {translate('Error')}: {translate('Your account is now locked to protect your information.')}
                        {' '}<Link to="/unlock-account">{translate('Unlock My Account')}.</Link>
                    </>;
                    analyticsMessage = `${translate('Error')}: ${translate('Your account is now locked to protect your information.')} ${translate('Unlock My Account')}`
                } else if (isDisabled){
                    message = <>
                        {translate('Error')}: {translate('Your account is no longer active. To re-open your account,')}
                        <a href={getChatUrl()}>{translate('chat with us')}</a> {translate('or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.')}
                    </>;
                    analyticsMessage = `${translate('Error')}: ${translate('Your account is no longer active. To re-open your account,')}
                  ${translate('chat with us')} ${translate('or call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.')}`
                } else {
                    message = translate('We’re not able to find a matching record for the information you provided. Please check the information and try again.');
                }
                setShowFlash(true);
                if (isLocked || isDisabled){
                    // @ts-ignore
                    conversion.failAPIIntermediate(analyticsMessage);
                    // @ts-ignore
                    conversion.failAPI(analyticsMessage);
                }
                // @ts-ignore
                conversion.failAPIIntermediate(message);
                // @ts-ignore
                conversion.failAPI(message);
                return setErrorMessage(message);
            } else if (result.code === 403) {
                setShowFlash(true);
                const message = translate(`We can't process your request at this time. We're sorry for any inconvenience. Please try again.`)
                conversion.failAPIIntermediate(message);
                conversion.failAPI(message);
                return setErrorMessage(message);
            } else if (result.code === 200) {
                conversion.success();
                props.setUserName(result.data['username']);
                props.setCreatedAt(result.data['createdAt']);
                props.showPageCallBack(1);
            }
        });
    };


    const updateDateOfBirth = (dateObject = {}) => {
        allow.anInstanceOf(dateObject, dateFieldValue);
        formStart();
        dateOfBirth.set(dateObject);
        if (validateEntireForm.get())
            validateForm();
    };

    const updateLastName = (value = '') => {
        allow.aString(value);
        formStart();
        const newValue = value.trimStart();
        lastName.set(newValue);
        validateLastName();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateMedicareNumber = (value = '', validate = false) => {
        allow.aString(value).aBoolean(validate);
        formStart();
        const newValue = value.trim();
        medicareNumber.set(newValue);
        if (!validate)
            return;
        validateMedicareNumber();
        if (validateEntireForm.get())
            validateForm();
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (!validateMedicareNumber())
            errorFieldRef = medicareNumberRef;
        if (!validateLastName())
            errorFieldRef = lastNameRef;
        if (dateOfBirth.get().month === '')
            errorFieldRef = monthRef;
        if (dateOfBirth.get().day === '')
            errorFieldRef = dayRef;
        if (!isValidYear(dateOfBirth.get().year))
            errorFieldRef = yearRef;
        const messages = getValidationAlertMessages();
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateLastName = () => {
        const isValid = lastName.get() !== '';
        if (lastNameError.get() === isValid)
            lastNameError.set(!isValid);
        return isValid;
    };

    const validateMedicareNumber = () => {
        const isValid = isValidMedicareNumber(medicareNumber.get());
        if (medicareNumberError.get() === isValid)
            medicareNumberError.set(!isValid);
        return isValid;
    };

    return <>
        <MedicareNumberDialog
            isOpen={dialogIsOpen.get()}
            onExit={() => dialogIsOpen.set(false)}
        />
        <Div
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow={true}>
                    <Div cols={12}>
                        <H1>
                            {capitalizeFirstLetter(translate('Find My Account').toLowerCase())}
                        </H1>
                        <Div style={{paddingBottom: 15}}>
                            <SubHeading style={{fontSize: 14, fontWeight: 400}}>
                                <TranslatedSpan english={'All fields required.'}/>
                            </SubHeading>
                        </Div>
                        <legend>
                            <Div style={{paddingBottom: 15}}>
                                <TranslatedSpan
                                    className={'.ds-text'}
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 400
                                    }}
                                    english={'Check to see if you already have an online Medicare account.'}
                                />
                            </Div>
                        </legend>
                        <ValidationAlerts
                            heading={`${translate('Error')}: ${translate('Complete these items to get your username.')}`}
                            validationMessages={getValidationAlertMessages()}
                        />
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div cols={12} colsSM={11} colsMD={10} colsLG={9} colsXL={8}>
                        <Div style={{
                            paddingTop: 10,
                            marginLeft: 0
                        }}>
                            {showFlash ?
                                <Alert
                                    variation="error"
                                    hideIcon={true}>
                                    <p className="ds-c-alert__text">
                                        {errorMessage}
                                    </p>
                                </Alert> : <div></div>}
                        </Div>
                    </Div>
                </Div>
                <Div isRow={true}>
                    <Div
                        cols={12} colsSM={10} colsMD={8} colsLG={6} colsXL={5}
                        paddingRightLG={0}
                    >
                        <InputField
                            aria-invalid={medicareNumberError.get().toString()}
                            errorMessage={medicareNumberError.get() ? `${translate('Error')}: ${translate('Enter your Medicare Number.')}` : null}
                            inputRef={setMedicareNumberRef}
                            label={<MedicareNumberLabel onClick={() => dialogIsOpen.set(true)}/>}
                            name={'medicareNumber'}
                            onClick={formStart}
                            onFocus={formStart}
                            onBlur={event => updateMedicareNumber(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateMedicareNumber(event.currentTarget.value)}
                            style={{minWidth: '100%'}}
                            value={medicareNumber.get()}
                        />
                    </Div>
                </Div>
                {width > the.minimumMediumViewportWidth &&
            <Div isRow={true}>
                <Div
                    cols={10} colsSM={8} colsMD={6} colsLG={5} colsXL={4}
                    paddingRight={1}
                >
                    <InputField
                        aria-invalid={lastNameError.get().toString()}
                        errorMessage={lastNameError.get() ? `${translate('Error')}: ${translate('Enter your last name.')}` : null}
                        inputRef={setLastNameRef}
                        label={translate('Last name')}
                        name={'lastName'}
                        onClick={formStart}
                        onFocus={formStart}
                        onBlur={event => updateLastName(event.currentTarget.value)}
                        onChange={event => updateLastName(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={lastName.get()}/>
                </Div>
                <Div
                    cols={2} colsLG={1}
                    paddingLeft={0} paddingRightLG={0}
                >
                    <SuffixDropdown
                        onChange={event => suffix.set(event.currentTarget.value)}
                        style={{
                            paddingLeft: 8,
                            paddingRight: 20,
                        }}
                        value={suffix.get()}/>
                </Div>
            </Div>}
                {width < the.minimumMediumViewportWidth &&
            <Div isRow={true}>
                <Div
                    cols={12} colsSM={10} colsMD={8} colsLG={6} colsXL={5}
                    paddingRightLG={0}
                >
                    <InputField
                        aria-invalid={lastNameError.get().toString()}
                        errorMessage={lastNameError.get() ? `${translate('Error')}: ${translate('Enter your last name.')}` : null}
                        inputRef={setLastNameRef}
                        label={translate('Last name')}
                        name={'lastName'}
                        onClick={formStart}
                        onBlur={event => updateLastName(event.currentTarget.value)}
                        onChange={event => updateLastName(event.currentTarget.value)}
                        style={{minWidth: '100%'}}
                        value={lastName.get()}/>
                </Div>
                <Div
                    cols={12} colsSM={10} colsMD={8} colsLG={6} colsXL={5}
                    paddingRightLG={0}
                >
                    <SuffixDropdown
                        onChange={event => suffix.set(event.currentTarget.value)}
                        style={{
                            paddingLeft: 8,
                            paddingRight: 20,
                        }}
                        value={suffix.get()}/>
                </Div>
            </Div>
                }
                <Div isRow={true}>
                    <Div cols={12}>
                        <DateFields
                            date={dateOfBirth.get()}
                            dayErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: day})}`}
                            dayFieldRef={setDayRef}
                            forceValidation={validateEntireForm.get()}
                            hint={translate('Use the format MM/DD/YYYY')}
                            label={translate('Date of Birth')}
                            monthErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: month})}`}
                            monthFieldRef={setMonthRef}
                            onBlur={date => updateDateOfBirth(date)}
                            onChange={date => updateDateOfBirth(date)}
                            yearErrorMessage={`${translate('Error')}: ${translate('Enter the {{timePeriod}} you were born.', {timePeriod: year})}`}
                            yearFieldRef={setYearRef}/>
                    </Div>
                </Div>
            </fieldset>
            <StackingButtons
                button1={getCancelButton()}
                button2={getFindMyAccountButton()}
            />
        </Div>
    </>;
}